/**
 * Franchise create/edit form component
 */
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Franchise } from '@/types';
import { CreateFranchiseInput, UpdateFranchiseInput, useFranchise } from '@/contexts/FranchiseContext';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { toast } from '@/lib/toast';

interface FranchiseFormProps {
  franchise?: Franchise;
  ownerUserId: string;
  onSubmit: (data: CreateFranchiseInput | UpdateFranchiseInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

type PlanTier = 'free' | 'basic' | 'premium' | 'enterprise';

// Generate a temporary franchise ID for new franchises
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export function FranchiseForm({ franchise, ownerUserId, onSubmit, onCancel, isLoading }: FranchiseFormProps) {
  const { t } = useTranslation();
  const { uploadLogo } = useFranchise();
  const [uploading, setUploading] = useState(false);

  // Generate a temporary ID for new franchises to use for image uploads
  const tempFranchiseId = useMemo(() => franchise?.franchiseId || generateTempId(), [franchise?.franchiseId]);

  const [formData, setFormData] = useState<CreateFranchiseInput>({
    ownerUserId,
    name: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    logo: undefined,
    planTier: 'free' as PlanTier,
    isActive: true,
    schedule: {
      weekdays: '10:00 - 21:00',
      saturday: '10:00 - 20:00',
      sunday: 'Cerrado'
    },
    averageWaitTime: '15min',
  });

  useEffect(() => {
    if (franchise) {
      setFormData({
        ownerUserId: franchise.ownerUserId,
        name: franchise.name,
        description: franchise.description || '',
        email: franchise.email,
        phone: franchise.phone,
        website: franchise.website || '',
        logo: franchise.logo,
        planTier: franchise.planTier,
        isActive: franchise.isActive,
        schedule: franchise.schedule || {
          weekdays: '10:00 - 21:00',
          saturday: '10:00 - 20:00',
          sunday: 'Cerrado'
        },
        averageWaitTime: franchise.averageWaitTime || '15min',
      });
    }
  }, [franchise]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const handleLogoUpload = async (file: File) => {
    setUploading(true);
    try {
      // Use the generated temp ID for new franchises or the real ID for existing ones
      const url = await uploadLogo(tempFranchiseId, file);
      setFormData(prev => ({ ...prev, logo: url }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      // Show error to user
      toast.error('Error al subir el logo. Por favor, intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-ink">
        {franchise ? t('franchises.edit') : t('franchises.add')}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-2">
            {t('franchises.form.logo')}
          </label>
          <ImageUpload
            currentImage={formData.logo}
            onUpload={handleLogoUpload}
            uploading={uploading}
          />
        </div>

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-ink-muted mb-2">
            {t('franchises.form.name')} *
          </label>
          <input
            id="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder={t('franchises.form.namePlaceholder')}
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-ink-muted mb-2">
            {t('franchises.form.description')}
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder={t('franchises.form.descriptionPlaceholder')}
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink-muted mb-2">
            {t('franchises.form.email')} *
          </label>
          <input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder={t('franchises.form.emailPlaceholder')}
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-ink-muted mb-2">
            {t('franchises.form.phone')} *
          </label>
          <input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder={t('franchises.form.phonePlaceholder')}
          />
        </div>

        {/* Website */}
        <div>
          <label htmlFor="website" className="block text-sm font-medium text-ink-muted mb-2">
            {t('franchises.form.website')}
          </label>
          <input
            id="website"
            type="url"
            value={formData.website}
            onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder={t('franchises.form.websitePlaceholder')}
          />
        </div>

        {/* Landing Page Configuration */}
        <div className="border-t border-hairline pt-6">
          <h3 className="text-lg font-semibold text-ink mb-4">
            📅 Configuración de Landing Page
          </h3>

          {/* Schedule */}
          <div className="space-y-4 mb-4">
            <div>
              <label htmlFor="scheduleWeekdays" className="block text-sm font-medium text-ink-muted mb-2">
                Horario Lunes - Viernes
              </label>
              <input
                id="scheduleWeekdays"
                type="text"
                value={formData.schedule?.weekdays || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  schedule: { ...(prev.schedule || {}), weekdays: e.target.value } as any
                }))}
                className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
                placeholder="10:00 - 21:00"
              />
            </div>
            <div>
              <label htmlFor="scheduleSaturday" className="block text-sm font-medium text-ink-muted mb-2">
                Horario Sábado
              </label>
              <input
                id="scheduleSaturday"
                type="text"
                value={formData.schedule?.saturday || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  schedule: { ...(prev.schedule || {}), saturday: e.target.value } as any
                }))}
                className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
                placeholder="10:00 - 20:00"
              />
            </div>
            <div>
              <label htmlFor="scheduleSunday" className="block text-sm font-medium text-ink-muted mb-2">
                Horario Domingo
              </label>
              <input
                id="scheduleSunday"
                type="text"
                value={formData.schedule?.sunday || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  schedule: { ...(prev.schedule || {}), sunday: e.target.value } as any
                }))}
                className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
                placeholder="Cerrado"
              />
            </div>
          </div>

          {/* Average Wait Time */}
          <div>
            <label htmlFor="averageWaitTime" className="block text-sm font-medium text-ink-muted mb-2">
              ⏱️ Tiempo de Espera Media
            </label>
            <input
              id="averageWaitTime"
              type="text"
              value={formData.averageWaitTime || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, averageWaitTime: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="15min"
            />
            <p className="text-xs text-ink-subtle mt-1">
              Ejemplo: "15min", "20 minutos", "~15min"
            </p>
          </div>
        </div>

        {/* Plan Tier */}
        <div>
          <label htmlFor="planTier" className="block text-sm font-medium text-ink-muted mb-2">
            {t('franchises.form.planTier')} *
          </label>
          <select
            id="planTier"
            required
            value={formData.planTier}
            onChange={(e) => setFormData(prev => ({ ...prev, planTier: e.target.value as PlanTier }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
          >
            <option value="free">{t('franchises.planTiers.free')}</option>
            <option value="basic">{t('franchises.planTiers.basic')}</option>
            <option value="premium">{t('franchises.planTiers.premium')}</option>
            <option value="enterprise">{t('franchises.planTiers.enterprise')}</option>
          </select>
        </div>

        {/* Is Active */}
        <div className="flex items-center">
          <input
            id="isActive"
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            className="w-4 h-4 text-info border-hairline rounded focus:ring-focus"
          />
          <label htmlFor="isActive" className="ml-2 text-sm font-medium text-ink-muted">
            {t('franchises.form.isActive')}
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <Button type="submit" isLoading={isLoading} className="flex-1">
            {franchise ? t('franchises.form.updateButton') : t('franchises.form.createButton')}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading} className="flex-1">
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
